import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from './Toast';
import { ApiService } from '../utils/api';
import { formatDateTime } from '../utils/datetime';
import { certificatePdfBlob, certificateUserData, renderCertificate, runLimited } from '../utils/certificateRender';
import { ChevronLeft, ChevronRight, FileSpreadsheet, Loader2, Mail, Trash2 } from 'lucide-react';
import { eventRef } from '../utils/eventRef';

const STATUS_TEXT = { DRAFT: 'Not sent', SENDING: 'Sending', DONE: 'Sent', CANCELLED: 'Stopped', INTERRUPTED: 'Interrupted' };

const isCertificatePurpose = (p) => p.shouldPrintCertificate || p.purpose === 'certificate'
  || p.name?.toLowerCase().includes('certificate');

const Preview = ({ config, recipient, eventName }) => {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setFailed(false);
    renderCertificate(config, certificateUserData(recipient, eventName), 72)
      .then((canvas) => { if (!cancelled) setSrc(canvas.toDataURL('image/png')); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [config, recipient, eventName]);
  if (failed) {
    return <p className="text-sm text-red-600">Could not draw the preview. If the design has a background image, its host must allow cross-origin loading.</p>;
  }
  return src
    ? <img src={src} alt={`Certificate for ${recipient.name}`} className="w-full border rounded shadow-sm" />
    : <div className="h-48 flex items-center justify-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
};

const CertificateListPanel = ({ eventId, eventName }) => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [purpose, setPurpose] = useState('certificate');
  const [batchId, setBatchId] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [skipped, setSkipped] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [confirmSend, setConfirmSend] = useState(false);

  const { data: purposes = [] } = useQuery({
    queryKey: ['qrPurposes', eventId],
    queryFn: () => ApiService.getQRScanPurposes(eventId),
  });
  const purposeOptions = useMemo(() => {
    const options = purposes.filter(isCertificatePurpose).map((p) => ({ value: p.purpose, label: p.name || p.purpose }));
    return options.some((o) => o.value === 'certificate') ? options : [{ value: 'certificate', label: 'Certificate' }, ...options];
  }, [purposes]);

  const { data: config, isLoading: configLoading } = useQuery({
    queryKey: ['badgeDesignerConfig', eventId, purpose],
    queryFn: () => ApiService.getBadgeDesignerConfig(eventId, purpose),
  });

  const { data: batches = [] } = useQuery({
    queryKey: ['certificateBatches', eventId],
    queryFn: () => ApiService.getCertificateBatches(eventId),
  });

  const { data: batch } = useQuery({
    queryKey: ['certificateBatch', eventId, batchId],
    queryFn: () => ApiService.getCertificateBatch(eventId, batchId),
    enabled: !!batchId,
    refetchInterval: (query) => (query.state.data?.status === 'SENDING' ? 3000 : false),
  });

  useEffect(() => {
    if (!batch) return;
    setSubject((prev) => prev || batch.subject || '');
    setBody((prev) => prev || (batch.body || '').replace(/<\/p><p>/g, '\n\n').replace(/<\/?p>/g, ''));
    if (batch.status !== 'SENDING') queryClient.invalidateQueries({ queryKey: ['certificateBatches', eventId] });
  }, [batch, eventId, queryClient]);

  const refreshBatch = () => {
    queryClient.invalidateQueries({ queryKey: ['certificateBatch', eventId, batchId] });
    queryClient.invalidateQueries({ queryKey: ['certificateBatches', eventId] });
  };

  const openBatch = (id) => {
    setBatchId(id);
    setPreviewIndex(0);
    setSubject('');
    setBody('');
    setConfirmSend(false);
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const created = await ApiService.createCertificateBatch(eventId, file, purpose);
      setSkipped(created.skipped || []);
      openBatch(created.batch.id);
      queryClient.invalidateQueries({ queryKey: ['certificateBatches', eventId] });
      showSuccess(`${created.batch.recipients.length} people loaded${created.skipped?.length ? `, ${created.skipped.length} rows skipped` : ''}.`);
    } catch (e) {
      showError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const recipients = batch?.recipients || [];
  const pending = recipients.map((r, index) => ({ r, index })).filter(({ r }) => !r.fileKey);
  const generatedCount = recipients.length - pending.length;
  const readyToSend = recipients.filter((r) => r.fileKey && r.status !== 'SENT').length;
  const sentCount = recipients.filter((r) => r.status === 'SENT').length;
  const failed = recipients.map((r, index) => ({ ...r, index })).filter((r) => r.status === 'FAILED');

  const generate = async (all) => {
    const targets = all ? recipients.map((r, index) => ({ r, index })) : pending;
    if (!targets.length) return;
    setProgress({ done: 0, total: targets.length });
    const failures = await runLimited(targets, 3, async ({ r, index }) => {
      const blob = await certificatePdfBlob(config, certificateUserData(r, eventName));
      await ApiService.uploadCertificateBatchFile(eventId, batchId, index, blob);
    }, (done) => setProgress({ done, total: targets.length }));
    setProgress(null);
    refreshBatch();
    if (failures.length) showError(`${failures.length} certificate(s) failed: ${failures[0].error.message}`);
    else showSuccess(`${targets.length} certificate(s) generated. Check a few, then send.`);
  };

  const send = async () => {
    try {
      const html = body.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
      await ApiService.sendCertificateBatch(eventId, batchId, subject, html);
      setConfirmSend(false);
      refreshBatch();
      showSuccess('Sending started. You can leave this page.');
    } catch (e) {
      showError(e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this list and its generated certificates?')) return;
    try {
      await ApiService.deleteCertificateBatch(eventId, id);
      if (id === batchId) setBatchId(null);
      queryClient.invalidateQueries({ queryKey: ['certificateBatches', eventId] });
    } catch (e) {
      showError(e.message);
    }
  };

  const busy = !!progress || batch?.status === 'SENDING';
  const previewRecipient = recipients[Math.min(previewIndex, recipients.length - 1)];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
          <span>Certificates from a name list</span>
        </CardTitle>
        <p className="text-sm text-gray-600">
          Upload an Excel or CSV file with <b>Name</b> and <b>Email</b> columns. Certificates use the design from the badge
          designer; other columns can be placed on it by their header. Generate and check them first, then send.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-gray-600 mb-1">Design</span>
            <select className="h-9 rounded-md border border-gray-300 px-2 text-sm" value={purpose}
              onChange={(e) => setPurpose(e.target.value)} disabled={busy}>
              {purposeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className={`inline-flex items-center h-9 px-3 rounded-md text-sm text-white cursor-pointer ${uploading || !config ? 'bg-gray-400 pointer-events-none' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
            Upload list (.xlsx / .csv)
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleUpload} disabled={uploading || !config} />
          </label>
        </div>
        {!configLoading && !config && (
          <p className="text-sm text-amber-700">
            No design saved for this purpose yet. <Link className="underline" to={`/admin/events/${eventRef(eventId)}/badges`}>Open the badge designer</Link> and save one for “{purpose}”.
          </p>
        )}
        {skipped.length > 0 && (
          <details className="text-sm text-amber-700">
            <summary className="cursor-pointer">{skipped.length} row(s) skipped</summary>
            <ul className="mt-1 list-disc pl-5">{skipped.slice(0, 50).map((s) => <li key={s}>{s}</li>)}</ul>
          </details>
        )}

        {batches.length > 0 && (
          <ul className="divide-y divide-gray-100 rounded border border-gray-200 text-sm">
            {batches.map((b) => (
              <li key={b.id} className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 ${b.id === batchId ? 'bg-blue-50' : ''}`}>
                <button type="button" className="text-left min-w-0 flex-1" onClick={() => openBatch(b.id)}>
                  <div className="font-medium truncate">{b.name}</div>
                  <div className="text-xs text-gray-500">
                    {formatDateTime(b.createdAt)} · {b.total} people · {b.generated} generated · {b.sent} sent
                    {b.failed ? ` · ${b.failed} failed` : ''} · {STATUS_TEXT[b.status] || b.status}
                  </div>
                </button>
                <Button variant="ghost" size="sm" onClick={() => remove(b.id)} disabled={b.status === 'SENDING'} title="Delete list">
                  <Trash2 className="w-4 h-4 text-gray-500" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {batch && config && (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              {previewRecipient && <Preview config={config} recipient={previewRecipient} eventName={eventName} />}
              <div className="flex items-center justify-between text-sm">
                <Button variant="outline" size="sm" disabled={previewIndex === 0} onClick={() => setPreviewIndex((i) => i - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="truncate px-2">{previewIndex + 1}/{recipients.length} · {previewRecipient?.name} · {previewRecipient?.email}</span>
                <Button variant="outline" size="sm" disabled={previewIndex >= recipients.length - 1} onClick={() => setPreviewIndex((i) => i + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              <div className="text-sm text-gray-700">
                {generatedCount}/{recipients.length} generated · {sentCount} sent{failed.length ? ` · ${failed.length} failed` : ''}
              </div>
              {progress ? (
                <div>
                  <div className="h-2 rounded bg-gray-200 overflow-hidden">
                    <div className="h-full bg-blue-600" style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Generating {progress.done}/{progress.total}… keep this tab open.</div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {pending.length > 0 && (
                    <Button size="sm" onClick={() => generate(false)} disabled={busy}>Generate {pending.length} certificate(s)</Button>
                  )}
                  {generatedCount > 0 && (
                    <Button size="sm" variant="outline" onClick={() => generate(true)} disabled={busy}>Regenerate all</Button>
                  )}
                </div>
              )}
              <div className="space-y-2 border-t pt-3">
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" disabled={busy} />
                <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} disabled={busy} />
                <p className="text-xs text-gray-500">
                  Placeholders: {'${name}'}, {'${email}'}, {'${eventName}'} and any column header in lower case, e.g. {'${organisation}'}.
                  Sent from the event's email account with its CC list.
                </p>
                {batch.status === 'SENDING' ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending… {sentCount} sent
                    <Button size="sm" variant="outline" onClick={() => ApiService.cancelCertificateBatch(eventId, batchId).then(refreshBatch)}>Stop</Button>
                  </div>
                ) : confirmSend ? (
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span>Email {readyToSend} certificate(s)?</span>
                    <Button size="sm" onClick={send}>Yes, send</Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirmSend(false)}>No</Button>
                  </div>
                ) : (
                  <Button size="sm" onClick={() => setConfirmSend(true)} disabled={busy || !readyToSend || !subject.trim() || !body.trim()}>
                    <Mail className="w-4 h-4 mr-2" /> Send {readyToSend || ''} certificate(s)
                  </Button>
                )}
                {failed.length > 0 && (
                  <details className="text-xs text-red-700">
                    <summary className="cursor-pointer">{failed.length} failed (sending again retries them)</summary>
                    <ul className="mt-1 space-y-0.5">{failed.slice(0, 50).map((r) => <li key={r.index}>{r.email}: {r.error}</li>)}</ul>
                  </details>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CertificateListPanel;
